// Third Party Libraries Imports
import toast from "react-hot-toast";
import { AxiosResponse } from "axios";
import { saveAs } from "file-saver";

// Local Imports
import instance from "../../store/axios";
import { LOADING } from "../../store/actions";
import {
  BulkMode,
  BulkResult,
  BulkUploadResponse,
} from "./types";

/**
 * Same module as the existing agreements list
 * (portfolio-agreement.service.ts): `/portfolio/new-portfolio-agreements`.
 */
const BASE = `/${process.env.REACT_APP_ENDPOINT_VERSION}/portfolio/new-portfolio-agreements`;

export const TEMPLATE_PATH = `${BASE}/template`;
export const UPLOAD_PATH = `${BASE}/upload`;

/** Multipart field name carrying the workbook. */
const FILE_FIELD = "file";

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** Base64 → bytes. Writing the string itself would produce a broken file. */
const base64ToBlob = (base64: string): Blob => {
  const clean = base64.includes(",")
    ? base64.slice(base64.indexOf(",") + 1)
    : base64;
  const binary = atob(clean.replace(/\s/g, ""));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: XLSX_MIME });
};

const apiMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

/**
 * Unwraps this API's response envelope.
 *
 * Most endpoints answer `{ data: <payload>, message }` — the agreements list
 * is read as `response.data.data.portfolios`, and the client-IP endpoint puts
 * its base64 straight in `data`. A few answer with the payload at the top
 * level. Rather than pick one and break on the other, this returns the
 * envelope's `data` when there is one and the body itself otherwise.
 */
const unwrap = <T,>(body: any): T => {
  if (body && typeof body === "object" && "data" in body) {
    const inner = (body as any).data;
    if (inner && typeof inner === "object") return inner as T;
  }
  return body as T;
};

/** Pulls the base64 workbook out, wherever the envelope put it. */
const templateFile = (body: any): string | undefined => {
  const candidates = [
    body?.file,
    body?.data?.file,
    // The client-IP endpoint's shape: the base64 sits directly in `data`.
    typeof body?.data === "string" ? body.data : undefined,
    typeof body === "string" ? body : undefined,
  ];
  return candidates.find(
    (value) => typeof value === "string" && value.length > 0
  );
};

class PortfolioAgreementsBulkService {
  private dispatch: any;

  constructor(dispatch?: any) {
    this.dispatch = dispatch;
  }

  private setLoading = (loading: boolean) => {
    if (this.dispatch) this.dispatch({ type: LOADING, payload: loading });
  };

  /**
   * `GET …/template` → `{ file: "<base64-xlsx>" }`. The workbook carries the
   * Records and Instructions worksheets operations fill in.
   */
  public async downloadTemplate(): Promise<boolean> {
    this.setLoading(true);
    try {
      const response: AxiosResponse<any> = await instance.get(TEMPLATE_PATH);

      const base64 = templateFile(response.data);
      if (!base64) {
        // Name what did come back — a silent "no template" is undebuggable.
        const keys =
          response.data && typeof response.data === "object"
            ? Object.keys(response.data).join(", ")
            : typeof response.data;
        toast.error(
          response.data?.message ||
            `The server did not return a template (response had: ${keys}).`
        );
        return false;
      }

      saveAs(base64ToBlob(base64), "portfolio-agreements-template.xlsx");
      return true;
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(apiMessage(error, "Failed to download the template"));
      }
      localStorage.setItem("star_prev_url", "/portfolio/agreements-bulk");
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * `POST …/upload?mode=validate|execute` with one multipart `file` field.
   *
   * Validate is read-only, so it can be run as often as needed. Execute
   * revalidates transactionally, which is why its response — not the earlier
   * validate response — is what the UI reports afterwards.
   */
  public async upload(
    file: File,
    mode: BulkMode
  ): Promise<BulkResult | undefined> {
    this.setLoading(true);

    const formData = new FormData();
    formData.append(FILE_FIELD, file);

    try {
      const response: AxiosResponse<BulkUploadResponse> = await instance.post(
        `${UPLOAD_PATH}?mode=${mode}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const body = unwrap<BulkUploadResponse>(response.data ?? {});
      const validRecords = body.validRecords ?? [];
      const excludedRecords = body.excludedRecords ?? [];

      // Counts may sit at the top level or under a summary object; fall back
      // to the array lengths so the summary is never blank.
      const nested = body.counts ?? body.summary ?? {};
      const counts = {
        total: body.total ?? nested.total,
        valid: body.valid ?? nested.valid ?? validRecords.length,
        excluded: body.excluded ?? nested.excluded ?? excludedRecords.length,
        inserted: body.inserted ?? nested.inserted,
      };

      return { mode, counts, validRecords, excludedRecords };
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(
          apiMessage(
            error,
            mode === "validate"
              ? "Failed to validate the file"
              : "Failed to execute the upload"
          )
        );
      }
      localStorage.setItem("star_prev_url", "/portfolio/agreements-bulk");
      return undefined;
    } finally {
      this.setLoading(false);
    }
  }
}

export default PortfolioAgreementsBulkService;
